/**
 * Onboarding State — V16 新手引导状态机
 *
 * 职责：
 *   - 管理引导流程的步骤流转（17 个步骤）
 *   - 持久化到 localStorage（刷新恢复）
 *   - 新用户检测
 *   - 跳过/完成控制
 *
 * 不依赖任何其他模块，纯函数 + localStorage。
 */

// ===== 步骤枚举 =====

export type OnboardingStep =
  | "home"           // 首页（简化模式）
  | "enlist"         // 录取通知书
  | "demo"           // 3v3 演示回放
  | "demo-end"       // 演示结束页
  | "forge-ch0"      // 铸剑阁第零章入口
  | "ed-2-1"         // 编辑器：作战简报
  | "ed-2-2"         // 编辑器：功能快速说明
  | "ed-2-3"         // 编辑器：补全代码
  | "ed-2-4"         // 编辑器：TEST 验证
  | "ed-2-5"         // 编辑器：DEPLOY 出战
  | "battle"         // 战斗（现有屏 4）
  | "rs-4-1"         // 结果：胜利降临
  | "rs-4-2"         // 结果：战斗分析
  | "rs-4-3"         // 结果：AAR + 顾问
  | "rs-4-4"         // 结果：CTA + Commit
  | "rs-4-5"         // 结果：刃师痕迹
  | "done";          // 完成 → 回铸剑阁

// ===== 持久化键 =====

const STATE_KEY = "battlecode-onboarding-state";
const COMPLETED_KEY = "battlecode-onboarding-completed";

// ===== 数据结构 =====

export interface OnboardingStateData {
  step: OnboardingStep;
  startTime: number;
  playerName: string;
}

// ===== API =====

/** 获取当前引导状态（如果正在进行中） */
export function getOnboardingState(): OnboardingStateData | null {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as OnboardingStateData;
    if (!data.step || data.step === "done") return null;
    return data;
  } catch {
    return null;
  }
}

/** 设置当前步骤 */
export function setOnboardingStep(step: OnboardingStep): void {
  const existing = getOnboardingState();
  const data: OnboardingStateData = existing ?? {
    step,
    startTime: Date.now(),
    playerName: "张宁",
  };
  data.step = step;
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(data));
  } catch {
    // localStorage 不可用时静默失败，引导流程仍可在内存中运行
  }
}

/** 设置玩家姓名 */
export function setOnboardingPlayerName(name: string): void {
  const existing = getOnboardingState();
  if (existing) {
    existing.playerName = name;
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(existing));
    } catch { /* noop */ }
  }
}

/** 获取玩家姓名 */
export function getOnboardingPlayerName(): string {
  return getOnboardingState()?.playerName ?? "张宁";
}

/** 当前是否在引导流程中 */
export function isOnboarding(): boolean {
  return getOnboardingState() !== null;
}

/** 当前是否在指定步骤 */
export function isOnboardingStep(step: OnboardingStep): boolean {
  const state = getOnboardingState();
  return state?.step === step;
}

/** 当前是否在编辑器引导阶段（2-1 ~ 2-5） */
export function isEditorOnboarding(): boolean {
  const state = getOnboardingState();
  if (!state) return false;
  return state.step.startsWith("ed-");
}

/** 当前是否在结果页引导阶段（4-1 ~ 4-5） */
export function isResultOnboarding(): boolean {
  const state = getOnboardingState();
  if (!state) return false;
  return state.step.startsWith("rs-");
}

/** 完成引导流程 */
export function completeOnboarding(): void {
  try {
    localStorage.setItem(COMPLETED_KEY, "1");
    localStorage.removeItem(STATE_KEY);
  } catch { /* noop */ }
}

/** 检测是否为新用户（需要显示引导） */
export function shouldShowOnboarding(): boolean {
  try {
    // 已完成引导 → 不再显示
    if (localStorage.getItem(COMPLETED_KEY)) return false;
    // 正在引导中 → 继续
    if (getOnboardingState()) return true;
    // 检查是否有任何进度数据（已有项目的用户不是新用户）
    const projectKeys = Object.keys(localStorage).filter(
      (k) => k.startsWith("battlecode-project-") || k.startsWith("battlecode-progress")
    );
    return projectKeys.length === 0;
  } catch {
    return false;
  }
}

/** 启动引导流程 */
export function startOnboarding(playerName: string): void {
  const data: OnboardingStateData = {
    step: "enlist",
    startTime: Date.now(),
    playerName,
  };
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(data));
  } catch { /* noop */ }
}

/** 跳过引导（直接完成） */
export function skipOnboarding(): void {
  completeOnboarding();
}

/**
 * V16 刷新恢复：根据当前 step 返回应跳转的 hash 路由。
 * 返回 null 表示留在首页重新开始（适用于 enlist/demo/demo-end 早期步骤）。
 */
export function getResumeRoute(): string | null {
  const state = getOnboardingState();
  if (!state) return null;

  const step = state.step;

  // 早期步骤：留在首页，重新走录取通知书→演示→锻剑阁
  if (step === "enlist" || step === "demo" || step === "demo-end") {
    return null;
  }

  // 锻剑阁步骤：直接进锻剑阁
  if (step === "forge-ch0") {
    return "#/forge";
  }

  // 编辑器步骤：直接进编辑器，由编辑器检测 onboarding 状态恢复引导
  if (step.startsWith("ed-")) {
    return "#/editor";
  }

  // 战斗/结果页步骤：上下文丢失无法恢复，完成引导回锻剑阁
  if (step === "battle" || step.startsWith("rs-")) {
    completeOnboarding();
    return "#/forge";
  }

  return null;
}
